import { createIntl, createIntlCache } from 'react-intl';
import firebase from 'firebase.js';

import english from 'languages/en';
import spanish from 'languages/es';
import en from 'assets/en.png';
import es from 'assets/es.png';

import moment from 'moment';

export const FIREBASE_RESPONSE = {
  EMAIL_IN_USE: 'auth/email-already-exists',
  EMAIL_INVALID: 'auth/invalid-email',
  EMAIL_NOT_FOUND: 'auth/user-not-found',
  PASSWORD_INVALID: 'auth/wrong-password',
  USER_DISABLED: 'auth/user-disabled',
  TOO_MANY_REQUESTS: 'auth/too-many-requests',
  EXPIRED_ACTION_CODE: 'auth/expired-action-code',
  INVALID_ACTION_CODE: 'auth/invalid-action-code',
  QUOTA_EXCEEDED_STORAGE: 'storage/quota-exceeded',
  UNAUTHENTICATED_STORAGE: 'storage/unauthenticated',
  UNAUTHORIZED_STORAGE: 'storage/unauthorized',
};

export const messages = {
  en: english,
  es: spanish,
};

const getIntlContext = (locale) => {
  const cache = createIntlCache();
  return createIntl(
    {
      locale,
      messages: messages[locale],
    },
    cache
  );
};

export const firebaseError = (error, locale) => {
  const intl = getIntlContext(locale);
  return intl.formatMessage({
    id: error,
    defaultMessage: messages[locale]['utils.default'],
  });
};

export const availableLocales = Object.keys(messages);

export const browserLocale = navigator.language.split(/[-_]/)[0];

export const flags = {
  en,
  es,
};

export const uiConfig = (onSignInSuccessHandler, onSignInFailHandler) => {
  return {
    callbacks: {
      signInSuccessWithAuthResult: onSignInSuccessHandler,
      signInFailure: onSignInFailHandler,
    },
    signInFlow: 'popup',
    signInSuccessUrl: '/home',
    signInOptions: [
      {
        provider: firebase.auth.GoogleAuthProvider.PROVIDER_ID,
        fullLabel: 'Continue with Google',
        scopes: [
          'https://www.googleapis.com/auth/user.addresses.read',
          'https://www.googleapis.com/auth/userinfo.email',
        ],
      },
      {
        provider: firebase.auth.FacebookAuthProvider.PROVIDER_ID,
        fullLabel: 'Continue with Facebook',
        scopes: ['email'],
      },
      { provider: 'microsoft.com', fullLabel: 'Continue with Microsoft' },
    ],
  };
};

export const isObject = (value) => {
  return value && typeof value === 'object' && value.constructor === Object;
};

export const search = (element, value) => {
  return Object.values(element).some((field) => {
    if (Array.isArray(field)) {
      return Object.values(field).some((item) => {
        return search(item, value);
      });
    }
    if (isObject(field)) {
      return search(field, value);
    }

    const fieldAsString = String(field); // Cast into string to compare lowercase values
    const res = fieldAsString.toLowerCase().includes(value.toLowerCase());
    return res;
  });
};

const getLastBatteryValue = (lastStatus) => {
  const defaultValue = 'N/A';
  const lastValue = lastStatus && lastStatus.status && lastStatus.status.value;

  if (lastValue) {
    return `${lastValue}V`;
  }
  return defaultValue;
};

const getLastLNBTemperatureValue = (lastStatus) => {
  const defaultValue = 'N/A';
  const lastValue =
    lastStatus && lastStatus.locales && lastStatus.locales.value;

  if (lastValue) {
    return `${lastValue}°C`;
  }
  return defaultValue;
};

const getLastTPHValue = (lastStatus) => {
  const defaultValue = 'N/A';
  const lastValue =
    lastStatus &&
    lastStatus.locales &&
    lastStatus.locales.temperature &&
    lastStatus.locales.temperature.value;

  if (lastValue) {
    return `${lastValue}°C`;
  }
  return defaultValue;
};

const getEpochStr = (epoch) => {
  const defaultValue = 'N/A';

  const epochStr = epoch && moment.utc(epoch).format('DD/MM/YYYY - HH[h]mm');

  if (epochStr) {
    return `${epochStr} UTC`;
  }
  return defaultValue;
};
/**
 *
 * @param {*} station
 * @param {
 *  signal:{ value: ...},
 *  status:{ value:...}
 *  locales: {lnbTemperature:{value:...}}
 * } lastStatus
 */
export const stationToMarker = (station, lastStatus) => {
  const signalStatus =
    lastStatus && lastStatus.signal && lastStatus.signal.status;

  const signalEpochStr =
    (lastStatus && lastStatus.signal && getEpochStr(lastStatus.signal.epoch)) ||
    'N/A';

  const batteryStatus =
    lastStatus && lastStatus.status && lastStatus.status.status;

  const batteryValue = getLastBatteryValue(lastStatus);

  const lnbTemperatureStatus =
    lastStatus && lastStatus.locales && lastStatus.locales.status;
  const lnbTemperature = getLastLNBTemperatureValue(lastStatus);
  const tphData = getLastTPHValue(lastStatus);

  const html = `
     <div> <h3 class="mb-1">
        <a href="/stations/${station.id}/profile" >
          Station #${station.serialNumber}
        </a>
      </h3>
      <p>
        <em>${station.name}</em>
      </p>
      <p>
        Réception des données : ${
          signalStatus || 'N/A'
        } - ${signalEpochStr}<br />
        Tension batterie : ${batteryStatus || 'N/A'} - ${batteryValue} <br />
        Temp. LNB : ${lnbTemperatureStatus || 'N/A'} - ${
    lnbTemperature || 'N/A'
  } <br />
        Capteur TPH : ${tphData}.
        <br/>
        Status : ${station.status}
      </p>
      </div>
    `;

  return {
    id: station.id,
    name: station.name,
    serialNumber: station.serialNumber,
    location: station.location,
    rotation: station.azimut,
    signalStatus,
    batteryStatus,
    html,
    status: station.status,
  };
};

export const parseArrayToDict = (array, key) => {
  const dict = {};
  array.forEach((item) => {
    const itemKey = item[key];
    if (itemKey) {
      dict[itemKey] = item;
    }
  });
  return dict;
};

export const parseDictToArray = (dict) => {
  return Object.values(dict);
};

export const removeEmpty = (obj) => {
  const newObj = {};

  if (Array.isArray(obj)) {
    return obj;
  }

  Object.keys(obj).forEach((key) => {
    if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
};

export const valueToSelectOption = (val) => {
  return { value: val, label: val };
};

export const selectOptionToValue = (option) => {
  return option && option.value;
};

export const degreeToRadian = (angleInDegree) => {
  return (angleInDegree / 180) * Math.PI;
};

export const radianToDegree = (angleInRadian) => {
  return (angleInRadian * 180) / Math.PI;
};
